import React from 'react'
import { BarChart3, CheckCircle, Play } from 'lucide-react'
import type { LessonActivity } from '@/types'
import { BlocklyReadOnlyDisplay } from '@/components/blockly/BlocklyReadOnlyDisplay'

export interface ActivityOutcomeData {
  activityTitle: string
  activityType: string
  responseCount: number
  playerCount: number
  bars: Array<{ label: string; count: number; color: string }>
  correctAnswerDisplay: string | null
  correctAnswerMono?: boolean
  correctAnswerXml?: string | null
  expectedCommand?: string | null
  explanation?: string | null
  explanation_image_url?: string | null
  hints?: string[] | null
}

const MULTI_QUESTION_TYPES = ['packet_analysis', 'cyber_hygiene_quiz', 'coding_quiz']

function getCSSColor(varName: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback
}

export function buildOutcomeData(
  activity: LessonActivity,
  responses: any[],
  playerCount: number,
): ActivityOutcomeData {
  const data = activity.activity_data || {}
  const type = activity.activity_type
  const bars: Array<{ label: string; count: number; color: string }> = []
  let correctAnswerDisplay: string | null = null
  let correctAnswerMono = false
  let correctAnswerXml: string | null = null
  let expectedCommand: string | null = null
  let explanation: string | null = null
  let hints: string[] | null = null

  const colorCorrect = getCSSColor('--color-success', '#22c55e')
  const colorOption = getCSSColor('--color-secondary', '#00ccff')
  const colorIncorrect = getCSSColor('--color-danger', '#ff6b6b')

  if (type === 'mcq') {
    // Tally selections per option; support both single-answer and multi-answer
    const options: string[] = data.options || []
    const correctIndices: Set<number> = (() => {
      if (Array.isArray(data.correct_answers) && data.correct_answers.length > 0) {
        return new Set<number>(data.correct_answers)
      }
      if (typeof data.correct_answer === 'number') return new Set<number>([data.correct_answer])
      return new Set<number>()
    })()
    const tally = new Array(options.length).fill(0)
    for (const r of responses) {
      const content = r.content || r.response_data || {}
      if (Array.isArray(content.selected_answers)) {
        for (const idx of content.selected_answers) {
          if (typeof idx === 'number' && idx >= 0 && idx < options.length) tally[idx]++
        }
      } else {
        const answer = content.answer
        if (typeof answer === 'number' && answer >= 0 && answer < options.length) {
          tally[answer]++
        } else if (typeof answer === 'string') {
          const idx = options.indexOf(answer)
          if (idx >= 0) tally[idx]++
        }
      }
    }
    for (let i = 0; i < options.length; i++) {
      bars.push({
        label: `${String.fromCharCode(65 + i)}. ${options[i]}`,
        count: tally[i],
        color: correctIndices.has(i) ? colorCorrect : colorOption,
      })
    }
    if (correctIndices.size > 0) {
      correctAnswerDisplay = [...correctIndices].sort((a, b) => a - b)
        .filter(i => options[i] !== undefined)
        .map(i => `${String.fromCharCode(65 + i)}. ${options[i]}`)
        .join(', ')
    }
  } else if (type === 'phishing_quiz') {
    // Tally content.answers[0].choice as "phishing"/"legitimate"
    let phishCount = 0
    let legitCount = 0
    for (const r of responses) {
      const content = r.content || r.response_data || {}
      const answers = content.answers || []
      const choice = answers[0]?.choice?.toLowerCase?.()
      if (choice === 'phishing') phishCount++
      else if (choice === 'legitimate') legitCount++
    }
    const correctType = data.type?.toLowerCase?.()
    bars.push({
      label: 'Phishing',
      count: phishCount,
      color: correctType === 'phishing' ? colorCorrect : colorOption,
    })
    bars.push({
      label: 'Legitimate',
      count: legitCount,
      color: correctType === 'legitimate' ? colorCorrect : colorOption,
    })
    correctAnswerDisplay = correctType === 'phishing'
      ? 'This email is PHISHING'
      : 'This email is LEGITIMATE'
    explanation = data.explanation || null
    hints = Array.isArray(data.hints) && data.hints.length > 0 ? data.hints : null
  } else if (MULTI_QUESTION_TYPES.includes(type)) {
    // Quiz types: options are shuffled per-player, so tally by option_text
    const options: string[] = data.options || []
    const correctIdx = data.correct_answer
    const tally = new Map<string, number>()
    for (const opt of options) tally.set(opt, 0)
    for (const r of responses) {
      const content = r.content || r.response_data || {}
      if (Array.isArray(content.answers) && content.answers.length > 0) {
        const text = content.answers[0].option_text
        if (typeof text === 'string' && tally.has(text)) {
          tally.set(text, (tally.get(text) || 0) + 1)
        }
      }
    }
    for (let i = 0; i < options.length; i++) {
      bars.push({
        label: `${String.fromCharCode(65 + i)}. ${options[i]}`,
        count: tally.get(options[i]) || 0,
        color: i === correctIdx ? colorCorrect : colorOption,
      })
    }
    if (typeof correctIdx === 'number' && options[correctIdx] !== undefined) {
      correctAnswerDisplay = `${String.fromCharCode(65 + correctIdx)}. ${options[correctIdx]}`
    }
  } else if (type === 'command_shell') {
    // score > 0 = correct, else incorrect
    let correct = 0
    let incorrect = 0
    for (const r of responses) {
      if ((r.score ?? 0) > 0) correct++
      else incorrect++
    }
    bars.push({ label: 'Correct', count: correct, color: colorCorrect })
    bars.push({ label: 'Incorrect', count: incorrect, color: colorIncorrect })
    if (data.correct_answer) {
      correctAnswerDisplay = data.correct_answer
      correctAnswerMono = true
    }
    if (data.command) {
      expectedCommand = data.command
    }
  } else if (type === 'python_repl') {
    let correct = 0
    let incorrect = 0
    for (const r of responses) {
      if ((r.score ?? 0) > 0) correct++
      else incorrect++
    }
    bars.push({ label: 'Correct', count: correct, color: colorCorrect })
    bars.push({ label: 'Incorrect', count: incorrect, color: colorIncorrect })
    // Show the correct solution code from activity_data
    const code = data.correct_code
    if (code) {
      correctAnswerDisplay = code
      correctAnswerMono = true
    }
  } else if (type === 'coding' || type === 'blockly') {
    let correct = 0
    let incorrect = 0
    for (const r of responses) {
      if ((r.score ?? 0) > 0) correct++
      else incorrect++
    }
    bars.push({ label: 'Correct', count: correct, color: colorCorrect })
    bars.push({ label: 'Incorrect', count: incorrect, color: colorIncorrect })
    // Show the correct solution as Blockly blocks (XML) or fallback to pseudocode
    const xml = data.correct_xml as string | undefined
    const code = data.correct_code
    if (xml) {
      correctAnswerXml = xml
    } else if (code) {
      correctAnswerDisplay = code
      correctAnswerMono = true
    }
  } else {
    // Fallback: correct vs incorrect by score
    let correct = 0
    let incorrect = 0
    for (const r of responses) {
      if ((r.score ?? 0) > 0) correct++
      else incorrect++
    }
    bars.push({ label: 'Correct', count: correct, color: colorCorrect })
    bars.push({ label: 'Incorrect', count: incorrect, color: colorIncorrect })
  }

  return {
    activityTitle: activity.title || `Activity ${activity.sequence_number || ''}`,
    activityType: type,
    responseCount: responses.length,
    playerCount,
    bars,
    correctAnswerDisplay,
    correctAnswerMono,
    correctAnswerXml,
    expectedCommand,
    explanation,
    explanation_image_url: data.explanation_image_url || null,
    hints,
  }
}

export function ActivityOutcome({ data, onShowResults }: {
  data: ActivityOutcomeData
  onShowResults: () => void
}) {
  const maxCount = Math.max(...data.bars.map(b => b.count), 1)

  return (
    <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center p-8 overflow-auto">
      <div className="text-center mb-8">
        <BarChart3 className="w-10 h-10 text-[var(--color-secondary)] mx-auto mb-3" />
        <h1 className="ds-page-title text-3xl mb-1">Activity Outcome</h1>
        <p className="text-lg text-theme-text-muted">"{data.activityTitle}"</p>
        <p className="text-sm text-theme-muted mt-1">
          {data.responseCount} response{data.responseCount !== 1 ? 's' : ''} from {data.playerCount} student{data.playerCount !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Answer distribution */}
      <div className="w-full max-w-2xl mb-8">
        <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-6 space-y-5">
          {data.bars.map((entry, idx) => {
            const pct = maxCount > 0 ? Math.round((entry.count / maxCount) * 100) : 0
            return (
              <div key={idx}>
                <div className="flex items-baseline justify-between gap-4 mb-1.5">
                  <span className="min-w-0 break-words text-base text-theme-text">{entry.label}</span>
                  <span className="shrink-0 text-sm font-semibold text-theme-text-muted tabular-nums">
                    {entry.count} response{entry.count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="h-5 w-full rounded-full bg-surface overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: entry.color,
                      minWidth: entry.count > 0 ? '0.5rem' : undefined,
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Expected Command (command_shell) */}
      {data.expectedCommand && (
        <div className="w-full max-w-2xl mb-4">
          <div className="bg-[var(--color-secondary-subtle)] border border-[var(--color-secondary)]/30 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-[var(--color-secondary)] uppercase tracking-wide">Expected Command</span>
            </div>
            <pre className="font-mono text-[var(--color-secondary)] text-base whitespace-pre-wrap">$ {data.expectedCommand}</pre>
          </div>
        </div>
      )}

      {/* Correct Answer — Blockly visual blocks */}
      {data.correctAnswerXml && (
        <div className="w-full max-w-2xl mb-8">
          <div className="bg-[var(--color-success-subtle)] border border-[var(--color-success)]/30 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-[var(--color-success)]" />
              <span className="text-sm font-semibold text-[var(--color-success)] uppercase tracking-wide">
                Correct Solution
              </span>
            </div>
            <BlocklyReadOnlyDisplay xml={data.correctAnswerXml} height={220} />
          </div>
        </div>
      )}

      {/* Correct Answer — text/code fallback */}
      {!data.correctAnswerXml && data.correctAnswerDisplay && (
        <div className="w-full max-w-2xl mb-8">
          <div className="bg-[var(--color-success-subtle)] border border-[var(--color-success)]/30 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-[var(--color-success)]" />
              <span className="text-sm font-semibold text-[var(--color-success)] uppercase tracking-wide">
                {data.activityType === 'phishing_quiz' ? 'Answer' : 'Correct Answer'}
              </span>
            </div>
            {data.correctAnswerMono ? (
              <pre className="font-mono text-[var(--color-success)] text-base whitespace-pre-wrap">{data.correctAnswerDisplay}</pre>
            ) : (
              <p className="text-[var(--color-success)] text-lg font-semibold">{data.correctAnswerDisplay}</p>
            )}
          </div>
        </div>
      )}

      {/* Explanation */}
      {(data.explanation || data.explanation_image_url) && (
        <div className="w-full max-w-2xl mb-4">
          <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5">
            <p className="text-sm font-semibold text-theme-text-muted uppercase tracking-wide mb-2">Explanation</p>
            {data.explanation && <p className="text-theme-text text-base">{data.explanation}</p>}
            {data.explanation_image_url && <img src={data.explanation_image_url} alt="" className="mt-3 max-w-full max-h-64 rounded-lg object-contain" />}
          </div>
        </div>
      )}

      {/* Hints (phishing quiz) */}
      {data.hints && data.hints.length > 0 && (
        <div className="w-full max-w-2xl mb-8">
          <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5">
            <p className="text-sm font-semibold text-theme-text-muted uppercase tracking-wide mb-2">What to look for</p>
            <ul className="space-y-1">
              {data.hints.map((hint, i) => (
                <li key={i} className="flex items-start gap-2 text-theme-text text-base">
                  <span className="text-[var(--color-secondary)] mt-0.5">→</span>
                  {hint}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Show Results button */}
      <button
        onClick={onShowResults}
        className="ds-btn ds-btn-primary ds-btn-lg hover:-translate-y-0.5 uppercase tracking-wide"
      >
        <Play className="w-5 h-5" />
        Show Results
      </button>
    </div>
  )
}
